import React from 'react';
import { useTranslation } from 'react-i18next';

const TimeSlotStats = ({ timeSlotData, slotReservationData, isLoading }) => {
  const { t } = useTranslation();
  const rowClasses =
    'grid grid-cols-[minmax(120px,1fr)_minmax(85px,95px)_repeat(3,minmax(65px,75px))] gap-1 items-center min-w-[460px]';

  // Skeleton loading for time slots column
  const SkeletonTimeSlots = ({ label }) => {
    return (
      <div className="min-w-0 mb-8 md:mb-0">
        <h3 className="text-xl font-semibold mb-4">{label}</h3>

        <div className="bg-teal-10/40 rounded-lg p-3 md:p-4 overflow-x-auto">
          <div className={`${rowClasses} mb-2`}>
            <span className="font-semibold text-xs">Heure</span>
            <span className="font-semibold text-xs text-center">Créneaux utilisés</span>
            <span className="font-semibold text-xs text-center">Arrivées</span>
            <span className="font-semibold text-xs text-center">Pique-niques</span>
            <span className="font-semibold text-xs text-center">Départs</span>
          </div>

          {/* Skeleton slots */}
          {[...Array(8)].map((_, index) => (
            <div key={index} className={`${rowClasses} py-1 text-xs font-semibold`}>
              <div className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-12 md:w-16"></div>
              {[...Array(4)].map((__, cellIndex) => (
                <div
                  key={cellIndex}
                  className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-6 md:w-8 mx-auto"></div>
              ))}
            </div>
          ))}

          <hr className="border-gray-300 my-2" />
          <div className={`${rowClasses} text-xs font-semibold text-blue-10`}>
            <span>Total</span>
            {[...Array(4)].map((_, cellIndex) => (
              <div
                key={cellIndex}
                className="h-3 bg-gradient-to-r from-teal-10/50 to-teal-10 rounded animate-pulse w-6 md:w-8 mx-auto"></div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="mt-8 md:mt-12">
        <h2 className="text-sm md:text-base text-gray-50 mb-4 px-2 md:px-0">
          Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
          d'une unité
        </h2>
        <div className="border border-gray-300 rounded-lg p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-gray-50">
            <SkeletonTimeSlots label="04.02" />
            <SkeletonTimeSlots label="05.02" />
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!timeSlotData) {
    return (
      <div className="mt-8 md:mt-12">
        <h2 className="text-sm md:text-base text-gray-50 mb-4 px-2 md:px-0">
          Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
          d'une unité
        </h2>
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 text-center text-gray-500 border border-gray-300">
          {t('timeSlotStats.noData') || 'Aucune donnée disponible'}
        </div>
      </div>
    );
  }

  // Format time for display (e.g. 09:00 → 09h00)
  const formatTime = (time) => {
    return time.replace(':', 'h');
  };

  // Function to render a single time slot column
  const renderTimeSlotColumn = (dayData, day) => {
    if (!dayData || !dayData.slots) return null;

    const slots = dayData.slots;
    const totals = dayData.totals || { arrival: 0, picnic: 0, departure: 0 };
    const reservations = slotReservationData?.[day];
    const reservationsByTime = new Map(reservations?.slots?.map((slot) => [slot.time, slot]));

    return (
      <div className="bg-teal-10 rounded-lg p-3 md:p-4 overflow-x-auto">
        <div className={`${rowClasses} mb-2`}>
          <span className="font-semibold text-xs">Heure</span>
          <span className="font-semibold text-xs text-center">Créneaux utilisés</span>
          <span className="font-semibold text-xs text-center">Arrivées</span>
          <span className="font-semibold text-xs text-center">Pique-niques</span>
          <span className="font-semibold text-xs text-center">Départs</span>
        </div>

        {slots.map((slot, index) => {
          const reservation = reservationsByTime.get(slot.time.replace(/h/g, ':'));
          return (
            <div key={index} className={`${rowClasses} py-1 text-xs font-semibold`}>
              <span className="text-gray-50">{formatTime(slot.time)}</span>
              <span className="text-center">
                {reservation
                  ? `${String(reservation.count).padStart(2, '0')}/${reservation.capacity}`
                  : '-'}
              </span>
              <span className="text-center">{slot.arrival > 0 ? slot.arrival : '-'}</span>
              <span className="text-center">{slot.picnic > 0 ? slot.picnic : '-'}</span>
              <span className="text-center">{slot.departure > 0 ? slot.departure : '-'}</span>
            </div>
          );
        })}

        <hr className="border-gray-300 my-2" />
        <div className={`${rowClasses} text-xs font-semibold text-blue-10`}>
          <span>Total</span>
          <span className="text-center">
            {reservations ? `${reservations.total}/${reservations.capacity}` : '-'}
          </span>
          <span className="text-center">{totals.arrival}</span>
          <span className="text-center">{totals.picnic}</span>
          <span className="text-center">{totals.departure}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-8 md:mt-12">
      <h2 className="text-sm md:text-base text-gray-50 mb-4 px-2 md:px-0">
        Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
        d'une unité
      </h2>

      <div className="border border-gray-300 rounded-lg p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 lg:gap-6 text-gray-50">
          <div className="min-w-0 mb-6 md:mb-0">
            <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">
              {timeSlotData.thu?.label || '04.02'}
            </h3>
            {renderTimeSlotColumn(timeSlotData.thu, 'thu')}
          </div>

          <div className="min-w-0 mb-6 md:mb-0">
            <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">
              {timeSlotData.fri?.label || '05.02'}
            </h3>
            {renderTimeSlotColumn(timeSlotData.fri, 'fri')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotStats;
